import { withRouter } from "react-router-dom";
import { withNamespaces } from "react-i18next";
import { connect } from "react-redux";
import { Button, Card, CardBody, Col, Label, Row, CardHeader, UncontrolledTooltip } from "reactstrap";
import { AvField, AvForm } from "availity-reactstrap-validation";
import Select from "react-select";
import { useContext, useEffect, useState } from "react";
import ProductContext from "../../../../contexts/ProductContext";
import DatePicker from "react-datepicker";
import * as datePickerLocales from "date-fns/locale";
import { INTERNATIONAL_DATE_FORMAT } from "src/components/constants";

const CustomDetails = ({
    t,
    lng,
    onNextButtonClicked,
    onBackButtonClicked,
    onCancelButtonClicked
}) => {
    const [importDate, setImportDate] = useState();
    const [exportDate, setExportDate] = useState();
    const [importFromCountry, setImportFromCountry] = useState();
    const [exportToCountry, setExportToCountry] = useState();

    const {
        fetchingDetails,
        productData,
        setProductData,

        countriesList
    } = useContext(ProductContext);

    useEffect(() => {
        if (!fetchingDetails) {
            if(productData?.importDate) {
                setImportDate(
                    new Date(productData.importDate)
                );
            }

            if(productData?.exportDate) {
                setExportDate(
                    new Date(productData.exportDate)
                );
            }

            if(productData?.importFromCountry) {
                setImportFromCountry(
                    productData.importFromCountry
                );
            }

            if(productData?.exportToCountry) {
                setExportToCountry(
                    productData.exportToCountry
                );
            }
        }
    }, [ productData, fetchingDetails ]);

    const handleSubmit = (e, values) => {
        setProductData((currentState) => {
            return { ...currentState, ...values }
        });

        onNextButtonClicked();
    }

    return (
        <Card>
            <CardHeader className="bg-white">
                <h5 className="mb-1">
                    {t('Trade & Customs Details')}
                </h5>
                <p className="text-muted mb-0">
                    {t('Enter import and export details, including dates, countries, and quantity. This helps trace the product’s movement across borders.')}
                </p>
            </CardHeader>

            <CardBody>
                <AvForm
                    className="needs-validation m-2"
                    onValidSubmit={handleSubmit}
                >
                    <Row>
                        <Col sm='12' md="6" className="mb-3">
                            <Label htmlFor="importDate">
                                {t('Date of Import')}
                            </Label>
                            <DatePicker
                                locale={datePickerLocales[lng]}
                                name="importDate"
                                id="importDate"
                                className="form-control"
                                selected={importDate}
                                showTimeSelect={false}
                                dateFormat={INTERNATIONAL_DATE_FORMAT}
                                onChange={(date) => {
                                    setImportDate(date);
                                }}
                                autoComplete='off'
                            />
                            <AvField
                                name="importDate"
                                type="hidden"
                                errorMessage={t("This field cannot be blank")}
                                className="form-control"
                                validate={{
                                    required: { value: true }
                                }}
                                value={importDate}
                            />
                        </Col>
                        <Col sm='12' md="6" className="mb-3">
                            <Label htmlFor="importFromCountry">
                                {t('Import from Country')}
                            </Label>
                            <Select
                                isLoading={countriesList.isFetching}
                                isDisabled={countriesList.isFetching}
                                id='importFromCountry'
                                placeholder={
                                    t('Country') + '...'
                                }
                                classNamePrefix='select2-selection'
                                options={countriesList.data}
                                onChange={setImportFromCountry}
                                value={importFromCountry}
                            />
                            <AvField
                                id="importFromCountry"
                                name="importFromCountry"
                                type="hidden"
                                errorMessage={t("This field cannot be blank")}
                                className="form-control"
                                validate={{
                                    required: { value: false }
                                }}
                                value={importFromCountry}
                            />
                        </Col>

                        <Col sm='12' md="6" className="mb-3">
                            <Label htmlFor="exportDate">
                                {t('Date of Export')}
                            </Label>
                            <DatePicker
                                locale={datePickerLocales[lng]}
                                name="exportDate"
                                id="exportDate"
                                className="form-control"
                                selected={exportDate}
                                showTimeSelect={false}
                                dateFormat={INTERNATIONAL_DATE_FORMAT}
                                onChange={(date) => {
                                    setExportDate(date);
                                }}
                                autoComplete='off'
                            />
                            <AvField
                                name="exportDate"
                                type="hidden"
                                errorMessage={t("This field cannot be blank")}
                                className="form-control"
                                validate={{
                                    required: { value: false }
                                }}
                                value={exportDate}
                            />
                        </Col>
                        <Col sm='12' md="6" className="mb-3">
                            <Label htmlFor="exportToCountry">
                                {t('Import to Country')}
                            </Label>
                            <Select
                                isLoading={countriesList.isFetching}
                                isDisabled={countriesList.isFetching}
                                id='exportToCountry'
                                placeholder={
                                    t('Country') + '...'
                                }
                                classNamePrefix='select2-selection'
                                options={countriesList.data}
                                onChange={setExportToCountry}
                                value={exportToCountry}
                            />
                            <AvField
                                id="exportToCountry"
                                name="exportToCountry"
                                type="hidden"
                                errorMessage={t("This field cannot be blank")}
                                className="form-control"
                                validate={{
                                    required: { value: false }
                                }}
                                value={exportToCountry}
                            />
                        </Col>

                        <Col sm='12' md="6" className="mb-3">
                            <Label htmlFor="containerNumber">
                                {t('Container Number')}
                            </Label>

                            <i
                                className="ri-information-line"
                                style={{
                                    cursor: 'pointer'
                                }}
                                id="containerNumberHelpText"
                            />

                            <UncontrolledTooltip 
                                target="containerNumberHelpText"
                            >
                                {t('The unique identifier for the shipping container.')}
                            </UncontrolledTooltip>

                            <AvField
                                id="containerNumber"
                                name="containerNumber"
                                type="text"
                                errorMessage={t("This field cannot be blank")}
                                className="form-control"
                                validate={{
                                    required: { value: false }
                                }}
                                value={productData?.containerNumber}
                            />
                        </Col>
                        <Col sm='12' md="6" className="mb-3">
                            <Label htmlFor="shipmentNumber">
                                {t('Shipment Number')}
                            </Label>

                            <i
                                className="ri-information-line"
                                style={{
                                    cursor: 'pointer'
                                }}
                                id="shipmentNumberHelpText"
                            />

                            <UncontrolledTooltip 
                                target="shipmentNumberHelpText"
                            >
                                {t('The reference number for this shipment.')}
                            </UncontrolledTooltip>

                            <AvField
                                id="shipmentNumber"
                                name="shipmentNumber"
                                type="text"
                                errorMessage={t("This field cannot be blank")}
                                className="form-control"
                                validate={{
                                    required: { value: false }
                                }}
                                value={productData?.shipmentNumber}
                            />
                        </Col>

                        <Col sm='12' md="3" className="mb-3">
                            <Label htmlFor="metricTonnes">
                                {t('Quantity (Metric Tonnes)')}
                            </Label>
                            <AvField
                                id="metricTonnes"
                                name="metricTonnes"
                                type="number"
                                errorMessage={t("This field cannot be blank")}
                                className="form-control"
                                validate={{
                                    required: { value: true }
                                }}
                                value={productData?.metricTonnes}
                            />
                        </Col>

                        <Col sm='12' md="3" className="mb-3">
                            <Label htmlFor="hsCode">
                                {t('HS Code (6-digit)')}
                            </Label>
                            <AvField
                                id="hsCode"
                                name="hsCode"
                                type="text"
                                errorMessage={t("This field cannot be blank")}
                                className="form-control"
                                validate={{
                                    required: { value: false }
                                }}
                                value={productData?.hsCode}
                            />
                        </Col>
                        <Col sm='12' md="3" className="mb-3">
                            <Label htmlFor="knCode">
                                {t('KN Code (8-digit)')}
                            </Label>
                            <AvField
                                id="knCode"
                                name="knCode"
                                type="text"
                                errorMessage={t("This field cannot be blank")}
                                className="form-control"
                                validate={{
                                    required: { value: false }
                                }}
                                value={productData?.knCode}
                            />
                        </Col>
                        <Col sm='12' md="3" className="mb-3">
                            <Label htmlFor="taricCode">
                                {t('TARIC Code (10-digit)')}
                            </Label>
                            <AvField
                                id="taricCode"
                                name="taricCode"
                                type="text"
                                errorMessage={t("This field cannot be blank")}
                                className="form-control"
                                validate={{
                                    required: { value: false }
                                }}
                                value={productData?.taricCode}
                            />
                        </Col>

                        <Col sm='12' className="d-flex justify-content-between">
                            <Button
                                color="secondary"
                                outline
                                type="button"
                                onClick={onCancelButtonClicked}
                            >
                                {t('Cancel')}
                            </Button>

                            <div>
                                <Button
                                    color="secondary"
                                    onClick={onBackButtonClicked}
                                    className="me-2"
                                    type="button"
                                >
                                    {t('Back')}
                                </Button>

                                <Button
                                    color="primary"
                                    type="submit"
                                >
                                    {t('Next')}
                                </Button>
                            </div>
                        </Col>
                    </Row>
                </AvForm>
            </CardBody>
        </Card>
    )
}

const mapStatetoProps = (state) => {
    const { token } = state.Login;
    return {
        user: state.Login.user,
        token: token
    };
};

export default withNamespaces()(
    withRouter(connect(
        mapStatetoProps,
        {}
    )(CustomDetails))
)